/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Android SDK", description = "Hightouch's Android SDK for collecting events", lastUpdatedDate = new Date(1792022400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Android SDK",
        description: "Hightouch's Android SDK for collecting events",
        lastUpdatedDate: "Thu Oct 15 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Installation",
          "depth": 0,
          "id": "installation"
        }, {
          "value": "Initialization",
          "depth": 0,
          "id": "initialization"
        }, {
          "value": "Context",
          "depth": 1,
          "id": "context"
        }, {
          "value": "Prebuilt plugins",
          "depth": 0,
          "id": "prebuilt-plugins"
        }, {
          "value": "Braze",
          "depth": 1,
          "id": "braze"
        }, {
          "value": "Session tracking",
          "depth": 0,
          "id": "session-tracking"
        }, {
          "value": "API",
          "depth": 0,
          "id": "api"
        }, {
          "value": "Identify",
          "depth": 1,
          "id": "identify"
        }, {
          "value": "Track",
          "depth": 1,
          "id": "track"
        }, {
          "value": "Screen",
          "depth": 1,
          "id": "screen"
        }, {
          "value": "Group",
          "depth": 1,
          "id": "group"
        }, {
          "value": "Flush",
          "depth": 1,
          "id": "flush"
        }, {
          "value": "Reset",
          "depth": 1,
          "id": "reset"
        }, {
          "value": "Opt out",
          "depth": 1,
          "id": "opt-out"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      a: "a",
      code: "code",
      pre: "pre",
      strong: "strong",
      span: "span",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      h3: "h3",
      h4: "h4",
      ul: "ul",
      li: "li",
      em: "em"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "The Android SDK makes it easy to track events from Java and Kotlin Android applications."
      }), "\n", _jsx(_components.h2, {
        id: "installation",
        children: "Installation"
      }), "\n", _jsxs(_components.p, {
        children: ["The Android SDK is published to ", _jsx(_components.a, {
          href: "https://central.sonatype.com/artifact/com.hightouch.analytics.android/analytics",
          children: "Maven Central"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Install it through Gradle. Include ", _jsx(_components.code, {
          children: "mavenCentral()"
        }), " in your dependency repositories:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "// settings.gradle\ndependencyResolutionManagement {\n  repositories {\n    google()\n    mavenCentral()\n  }\n}\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Replace ", _jsx(_components.code, {
          children: "HIGHTOUCH_VERSION"
        }), " with the released SDK version you want to use:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "// app/build.gradle\n\ndependencies {\n  implementation 'com.hightouch.analytics.android:analytics:HIGHTOUCH_VERSION'\n}\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Existing JitPack installations can switch to these Maven Central coordinates. Remove the old ", _jsx(_components.code, {
          children: "com.github.ht-sdks.events-sdk-android"
        }), " dependency when switching to avoid including the core SDK twice."]
      }), "\n", _jsx(_components.h2, {
        id: "initialization",
        children: "Initialization"
      }), "\n", _jsxs(_components.p, {
        children: ["You should initialize the SDK in a central location and store it in a variable\nfor use across your application. For example, you may want to initialize it in\nyour application's ", _jsx(_components.code, {
          children: "onCreate"
        }), " callback. After initialization, you can use the\nSDK anywhere you import the ", _jsx(_components.code, {
          children: "Analytics"
        }), " class via the singleton instance."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Kotlin example"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-scss",
          children: ["import com", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".hightouch"
          }), _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".analytics"
          }), _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".Analytics"
          }), "\n\nval analytics = Analytics", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".Builder"
          }), "(context, \"YOUR_WRITE_KEY\")\n    ", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".defaultApiHost"
          }), "(\"us-east-", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), ".hightouch-events.com/v1\")\n    ", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".trackApplicationLifecycleEvents"
          }), "()\n    ", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".build"
          }), "()\n\nAnalytics", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".setSingletonInstance"
          }), "(analytics)\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Java example"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-ini",
          children: ["import com.hightouch.analytics.Analytics\n\nAnalytics ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "analytics"
          }), " = new Analytics.Builder(context, ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"YOUR_WRITE_KEY\""
          }), ")\n    .defaultApiHost(\"us-east-1.hightouch-events.com/v1\")\n    .trackApplicationLifecycleEvents()\n    .build()", _jsx(_components.span, {
            className: "hljs-comment",
            children: ";"
          }), "\n\nAnalytics.setSingletonInstance(analytics)", _jsx(_components.span, {
            className: "hljs-comment",
            children: ";"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Configuration options"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Option"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "defaultApiHost"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The API host to send the tracked events to."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "flushQueueSize"
              })
            }), _jsx(_components.td, {
              children: "int"
            }), _jsx(_components.td, {
              children: "The number of events to queue before sending events to the Hightouch API. Defaults to 20."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "flushInterval"
              })
            }), _jsx(_components.td, {
              children: "int"
            }), _jsx(_components.td, {
              children: "The maximum amount of time (in seconds) to store events locally before forwarding to Hightouch. Defaults to 30 seconds."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "trackApplicationLifecycleEvents"
              })
            }), _jsx(_components.td, {
              children: "n/a"
            }), _jsx(_components.td, {
              children: "Automatically track application lifecycle events (like opening the application)."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "recordScreenViews"
              })
            }), _jsx(_components.td, {
              children: "n/a"
            }), _jsx(_components.td, {
              children: "Automatically track screen views."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "trackDeepLinks"
              })
            }), _jsx(_components.td, {
              children: "n/a"
            }), _jsx(_components.td, {
              children: "Automatically track deep links."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "foregroundSessionTimeout"
              })
            }), _jsx(_components.td, {
              children: "long"
            }), _jsxs(_components.td, {
              children: ["Maximum foreground inactivity in milliseconds before a new session starts. Defaults to ", _jsx(_components.code, {
                children: "1800000"
              }), " (30 minutes)."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "backgroundSessionTimeout"
              })
            }), _jsx(_components.td, {
              children: "long"
            }), _jsxs(_components.td, {
              children: ["Maximum time in the background, in milliseconds, before a new session starts on resume. Defaults to ", _jsx(_components.code, {
                children: "1800000"
              }), " (30 minutes)."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "context",
        children: "Context"
      }), "\n", _jsx(_components.p, {
        children: "By default, Hightouch automatically collects contextual metadata with each event, such as the device operating system. This context is forwarded with each event."
      }), "\n", _jsx(_components.h4, {
        id: "modify-global-context",
        children: "Modify global context"
      }), "\n", _jsxs(_components.p, {
        children: ["To customize the default context sent with every event, use the ", _jsx(_components.code, {
          children: "getAnalyticsContext()"
        }), " method. This allows you to add or remove metadata that will be included with all subsequent events."]
      }), "\n", _jsx(_components.p, {
        children: "For example, to set UTM campaign parameters globally:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-ini",
          children: ["AnalyticsContext ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "context"
          }), " = Analytics.with(context).getAnalyticsContext()", _jsx(_components.span, {
            className: "hljs-comment",
            children: ";"
          }), "\ncontext.putValue(\"custom_key\", \"custom_value\")", _jsx(_components.span, {
            className: "hljs-comment",
            children: ";"
          }), "\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "This applies the campaign data to every future event in the session."
      }), "\n", _jsx(_components.p, {
        children: "For more information, see:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://github.com/ht-sdks/events-sdk-android/blob/main/analytics/src/main/java/com/hightouch/analytics/AnalyticsContext.java",
            children: "AnalyticsContext.java"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://github.com/ht-sdks/events-sdk-android#context",
            children: "Android SDK README – Context"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "override-context-per-event",
        children: "Override context per event"
      }), "\n", _jsxs(_components.p, {
        children: ["You can override the context for an individual ", _jsx(_components.code, {
          children: "track()"
        }), " or ", _jsx(_components.code, {
          children: "identify()"
        }), " call by passing an ", _jsx(_components.code, {
          children: "Options"
        }), " object as the third argument. This is useful when you want to apply custom context — like UTM campaign parameters — to a single event."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-java",
          children: [_jsx(_components.span, {
            className: "hljs-type",
            children: "ValueMap"
          }), " ", _jsx(_components.span, {
            className: "hljs-variable",
            children: "campaign"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "new"
          }), " ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "ValueMap"
          }), "()\n    .putValue(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"utm_source\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"adwords\""
          }), ")\n    .putValue(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"utm_medium\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"cpc\""
          }), ")\n    .putValue(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"utm_campaign\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"product_launch\""
          }), ");\n\n", _jsx(_components.span, {
            className: "hljs-type",
            children: "ValueMap"
          }), " ", _jsx(_components.span, {
            className: "hljs-variable",
            children: "contextMap"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "new"
          }), " ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "ValueMap"
          }), "().putValue(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"campaign\""
          }), ", campaign);\n\n", _jsx(_components.span, {
            className: "hljs-type",
            children: "Options"
          }), " ", _jsx(_components.span, {
            className: "hljs-variable",
            children: "options"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "new"
          }), " ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Options"
          }), "().setContext(contextMap);\n\nAnalytics.with(context).track(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Product Viewed\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), ", options);\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "This will attach campaign context only to the specified event."
      }), "\n", _jsx(_components.p, {
        children: "For implementation details, see:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://github.com/ht-sdks/events-sdk-android/blob/main/analytics/src/main/java/com/hightouch/analytics/Options.java",
            children: "Options.java"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://github.com/ht-sdks/events-sdk-android#context",
            children: "Android SDK README – Context"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "prebuilt-plugins",
        children: "Prebuilt plugins"
      }), "\n", _jsx(_components.p, {
        children: "Use Hightouch's prebuilt plugins to add integrations without writing a plugin yourself. Optional plugins can require an additional artifact or destination SDK."
      }), "\n", _jsx(_components.h3, {
        id: "braze",
        children: "Braze"
      }), "\n", _jsx(_components.p, {
        children: "Use the Braze plugin to forward Hightouch calls to the Braze SDK on the device. This lets Braze evaluate in-app message triggers while the user is active, alongside normal event collection in Hightouch."
      }), "\n", _jsxs(_components.p, {
        children: ["Add the plugin and Braze SDK to your app's Gradle dependencies. Replace ", _jsx(_components.code, {
          children: "HIGHTOUCH_VERSION"
        }), " with ", _jsx(_components.code, {
          children: "0.2.0"
        }), " or later, using the same version for the plugin and your core Hightouch dependency:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "implementation 'com.hightouch.analytics.android:analytics-braze:HIGHTOUCH_VERSION'\nimplementation 'com.braze:android-sdk-base:41.1.1'\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The plugin does not bundle the Braze SDK. The example uses Braze 41.1.1, which requires Android API 29 or later. Use ", _jsx(_components.code, {
          children: "android-sdk-ui"
        }), " instead of ", _jsx(_components.code, {
          children: "android-sdk-base"
        }), " if your app needs Braze's default UI."]
      }), "\n", _jsxs(_components.p, {
        children: ["In your application's ", _jsx(_components.code, {
          children: "onCreate"
        }), ", register the integration when building your Hightouch client. Use your Braze SDK API key and SDK endpoint:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-kotlin",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " com.hightouch.analytics.Analytics\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " com.hightouch.analytics.braze.BrazeIntegration\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "val"
          }), " brazeFactory = BrazeIntegration.builder(\n    context, ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"YOUR_BRAZE_SDK_API_KEY\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"YOUR_BRAZE_SDK_ENDPOINT\""
          }), "\n)\n    .forwardScreenViews(", _jsx(_components.span, {
            className: "hljs-literal",
            children: "true"
          }), ")\n    .build()\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "val"
          }), " analytics = Analytics.Builder(context, ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"YOUR_WRITE_KEY\""
          }), ")\n    .defaultApiHost(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"us-east-1.hightouch-events.com/v1\""
          }), ")\n    .use(brazeFactory)\n    .build()\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The plugin forwards ", _jsx(_components.code, {
          children: "identify"
        }), " calls as user updates and ", _jsx(_components.code, {
          children: "track"
        }), " calls as custom events. By default, ", _jsx(_components.code, {
          children: "Order Completed"
        }), " and ", _jsx(_components.code, {
          children: "Completed Order"
        }), " become purchases, with one purchase per product. Screen forwarding is off by default; ", _jsx(_components.code, {
          children: ".forwardScreenViews(true)"
        }), " forwards ", _jsx(_components.code, {
          children: "screen"
        }), " calls as custom events."]
      }), "\n", _jsxs(_components.p, {
        children: ["Your application configures message presentation, Content Cards, and push notifications. The API-key setup above manages Braze sessions. If you supply an existing instance through ", _jsx(_components.code, {
          children: "BrazeIntegration.builder(context, existingBraze)"
        }), ", your application manages its sessions."]
      }), "\n", _jsxs(_components.p, {
        children: ["See the ", _jsx(_components.a, {
          href: "https://github.com/ht-sdks/events-sdk-android/tree/main/analytics-samples/braze-sample",
          children: "Braze sample"
        }), " for application and UI setup. If you're replacing an mParticle kit, follow ", _jsx(_components.a, {
          href: "/events/migration-guide/mparticle-braze",
          children: "Migrate the mParticle Braze kit"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "session-tracking",
        children: "Session tracking"
      }), "\n", _jsxs(_components.p, {
        children: ["Session tracking is enabled by default. The SDK attaches ", _jsx(_components.code, {
          children: "sessionId"
        }), ", ", _jsx(_components.code, {
          children: "sessionStart"
        }), ", and a ", _jsx(_components.code, {
          children: "session"
        }), " object to ", _jsx(_components.code, {
          children: "context"
        }), " on every event so you can group events from the same app session."]
      }), "\n", _jsx(_components.p, {
        children: "Sessions use two timeouts, both in milliseconds:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "foregroundSessionTimeout"
          }), " limits inactivity while the app is in the foreground."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "backgroundSessionTimeout"
          }), " limits how long the app can stay in the background before the next resume starts a new session."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Both default to ", _jsx(_components.code, {
          children: "1800000"
        }), " (30 minutes). Set both to ", _jsx(_components.code, {
          children: "0"
        }), " to disable session tracking."]
      }), "\n", _jsx(_components.p, {
        children: "A new session starts when any of these occur:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "First launch, when no session state is persisted."
        }), "\n", _jsxs(_components.li, {
          children: ["Foreground inactivity exceeds ", _jsx(_components.code, {
            children: "foregroundSessionTimeout"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Resume after being backgrounded longer than ", _jsx(_components.code, {
            children: "backgroundSessionTimeout"
          }), ", including after process death."]
        }), "\n", _jsxs(_components.li, {
          children: ["Calling ", _jsx(_components.code, {
            children: "reset()"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Session state is persisted across app relaunches. ", _jsx(_components.code, {
          children: "sessionIndex"
        }), " is a per-device lifetime counter. It starts at ", _jsx(_components.code, {
          children: "0"
        }), " and resets only when the app is uninstalled, not when you call ", _jsx(_components.code, {
          children: "reset()"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "You can change the timeouts when you initialize the SDK:"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Kotlin example"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-scss",
          children: ["val analytics = Analytics", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".Builder"
          }), "(context, \"YOUR_WRITE_KEY\")\n    ", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".foregroundSessionTimeout"
          }), "(", _jsx(_components.span, {
            className: "hljs-number",
            children: "60"
          }), " * ", _jsx(_components.span, {
            className: "hljs-number",
            children: "60"
          }), " * ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1000"
          }), ")\n    ", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".backgroundSessionTimeout"
          }), "(", _jsx(_components.span, {
            className: "hljs-number",
            children: "60"
          }), " * ", _jsx(_components.span, {
            className: "hljs-number",
            children: "60"
          }), " * ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1000"
          }), ")\n    ", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".build"
          }), "()\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Java example"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-ini",
          children: ["Analytics ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "analytics"
          }), " = new Analytics.Builder(context, ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"YOUR_WRITE_KEY\""
          }), ")\n    .foregroundSessionTimeout(60 * 60 * 1000)\n    .backgroundSessionTimeout(60 * 60 * 1000)\n    .build()", _jsx(_components.span, {
            className: "hljs-comment",
            children: ";"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.code, {
          children: "context.session"
        }), " includes:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Field"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "sessionId"
              })
            }), _jsx(_components.td, {
              children: "int"
            }), _jsx(_components.td, {
              children: "Current session ID (epoch milliseconds)."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "sessionIndex"
              })
            }), _jsx(_components.td, {
              children: "int"
            }), _jsxs(_components.td, {
              children: ["Per-device lifetime counter. Starts at ", _jsx(_components.code, {
                children: "0"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "sessionStart"
              })
            }), _jsx(_components.td, {
              children: "boolean"
            }), _jsxs(_components.td, {
              children: ["Present and ", _jsx(_components.code, {
                children: "true"
              }), " only on the first event of the session."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "eventIndex"
              })
            }), _jsx(_components.td, {
              children: "int"
            }), _jsx(_components.td, {
              children: "0-based index of the event within the session."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "previousSessionId"
              })
            }), _jsx(_components.td, {
              children: "int or null"
            }), _jsxs(_components.td, {
              children: ["Previous session ID. ", _jsx(_components.code, {
                children: "null"
              }), " on the first session."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "firstEventId"
              })
            }), _jsx(_components.td, {
              children: "UUID"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "messageId"
              }), " of the session's first event."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "firstEventTimestamp"
              })
            }), _jsx(_components.td, {
              children: "ISO 8601"
            }), _jsx(_components.td, {
              children: "When the session began."
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.code, {
          children: "sessionId"
        }), " and ", _jsx(_components.code, {
          children: "sessionStart"
        }), " are also set on ", _jsx(_components.code, {
          children: "context"
        }), " for parity with the ", _jsx(_components.a, {
          href: "/events/sdks/browser#session-tracking",
          children: "browser SDK"
        }), ". ", _jsx(_components.code, {
          children: "sessionStart"
        }), " is included only when it is ", _jsx(_components.code, {
          children: "true"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "api",
        children: "API"
      }), "\n", _jsx(_components.h3, {
        id: "identify",
        children: "Identify"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "identify"
        }), " method sends an ", _jsx(_components.code, {
          children: "identify"
        }), " event. It accepts user id and trait\ninformation for the current user -- if you only have partial information, you\ncan just supply a single parameter."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Java method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-csharp",
          children: ["Analytics.", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "with"
          }), "(context).identify(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"userId\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "new"
          }), " Traits().putEmail(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"email\""
          }), "), ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), ");\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Kotlin method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-csharp",
          children: ["Analytics.", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "with"
          }), "(context).identify(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"userId\""
          }), ", Traits().putEmail(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"email\""
          }), "), ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), ");\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "traits"
              })
            }), _jsx(_components.td, {
              children: "Traits (optional)"
            }), _jsxs(_components.td, {
              children: ["Additional traits about the user, such as ", _jsx(_components.code, {
                children: "email"
              }), " and ", _jsx(_components.code, {
                children: "name"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "options"
              })
            }), _jsx(_components.td, {
              children: "Options (optional)"
            }), _jsxs(_components.td, {
              children: ["Overrides to the event's context may be passed through the ", _jsx(_components.code, {
                children: "options"
              }), " object. This includes setting custom fields like ", _jsx(_components.a, {
                href: "#override-context-per-event",
                children: "campaign data"
              }), ". ", _jsx(_components.em, {
                children: "Note that this overrides the default context."
              })]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "track",
        children: "Track"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "track"
        }), " method sends a ", _jsx(_components.code, {
          children: "track"
        }), " event. The event name is required, and\nadditional properties that describe the event may be supplied."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Java method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-csharp",
          children: ["Analytics.", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "with"
          }), "(context).track(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"eventName\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "new"
          }), " Properties().putValue(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"custom\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"property\""
          }), "), ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), ");\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Kotlin method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-csharp",
          children: ["Analytics.", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "with"
          }), "(context).track(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"eventName\""
          }), ", Properties().putValue(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"custom\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"property\""
          }), "), ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), ");\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "name"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The name of the event."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "properties"
              })
            }), _jsx(_components.td, {
              children: "Properties (optional)"
            }), _jsxs(_components.td, {
              children: ["Additional properties about the event, such as ", _jsx(_components.code, {
                children: "product_id"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "options"
              })
            }), _jsx(_components.td, {
              children: "Options (optional)"
            }), _jsxs(_components.td, {
              children: ["Overrides to the event's context may be passed through the ", _jsx(_components.code, {
                children: "options"
              }), " object. Note that this overrides the default context."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "screen",
        children: "Screen"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "screen"
        }), " method sends a ", _jsx(_components.code, {
          children: "screen"
        }), " event. The screen title is required, and\nadditional information about the screen's category and properties may be\nsupplied."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Java method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-csharp",
          children: ["Analytics.", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "with"
          }), "(context).screen(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"category\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"name\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "new"
          }), " Properties().putValue(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"custom\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"property\""
          }), "), ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), ");\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Kotlin method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-csharp",
          children: ["Analytics.", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "with"
          }), "(context).screen(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"category\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"name\""
          }), ", Properties().putValue(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"custom\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"property\""
          }), "), ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), ");\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "category"
              })
            }), _jsx(_components.td, {
              children: "String (optional if name is supplied)"
            }), _jsx(_components.td, {
              children: "The screen's category. For example \"Docs\""
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "title"
              })
            }), _jsx(_components.td, {
              children: "String (optional if category is supplied)"
            }), _jsx(_components.td, {
              children: "The screen's name. For example \"Getting started\""
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "properties"
              })
            }), _jsx(_components.td, {
              children: "Properties (optional)"
            }), _jsxs(_components.td, {
              children: ["Additional properties about the event, such as ", _jsx(_components.code, {
                children: "from_link"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "options"
              })
            }), _jsx(_components.td, {
              children: "Options (optional)"
            }), _jsxs(_components.td, {
              children: ["Overrides to the event's context may be passed through the ", _jsx(_components.code, {
                children: "options"
              }), " object. Note that this overrides the default context."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "group",
        children: "Group"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "group"
        }), " method sends a ", _jsx(_components.code, {
          children: "group"
        }), " event."]
      }), "\n", _jsx(_components.p, {
        children: "The id identifying the user's group is required. Additional traits on the group\ncan be provided."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Java method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-csharp",
          children: ["Analytics.", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "with"
          }), "(context).", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "group"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"groupId\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "new"
          }), " Traits().putValue(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"custom\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"property\""
          }), "), ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), ");\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Kotlin method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-csharp",
          children: ["Analytics.", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "with"
          }), "(context).", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "group"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"groupId\""
          }), ", Traits().putValue(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"custom\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"property\""
          }), "), ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), ");\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "groupId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The id for the group."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "traits"
              })
            }), _jsx(_components.td, {
              children: "Traits (optional)"
            }), _jsxs(_components.td, {
              children: ["Additional traits about the group, such as ", _jsx(_components.code, {
                children: "company_name"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "options"
              })
            }), _jsx(_components.td, {
              children: "Options (optional)"
            }), _jsxs(_components.td, {
              children: ["Overrides to the event's context may be passed through the ", _jsx(_components.code, {
                children: "options"
              }), " object. Note that this overrides the default context."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "flush",
        children: "Flush"
      }), "\n", _jsx(_components.p, {
        children: "The Android SDK buffers events locally before sending them to Hightouch's servers.\nThis minimizes the number of requests made by the SDK and makes the tracking\nnon-blocking."
      }), "\n", _jsxs(_components.p, {
        children: ["To force the local buffer to be sent to Hightouch immediately call the ", _jsx(_components.code, {
          children: "flush"
        }), "\nmethod."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-scss",
          children: ["Analytics", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".with"
          }), "(context)", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".flush"
          }), "()\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "reset",
        children: "Reset"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "reset"
        }), " method resets the ", _jsx(_components.code, {
          children: "identify"
        }), " and ", _jsx(_components.code, {
          children: "group"
        }), " for the local session."]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "reset"
        }), " method should be called when users log out. This way, if the user\nlogs back in with another account, the userIds and traits from the different\nsessions remain separate. Calling ", _jsx(_components.code, {
          children: "reset()"
        }), " also starts a new analytics session.\nSee ", _jsx(_components.a, {
          href: "#session-tracking",
          children: "Session tracking"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-scss",
          children: ["Analytics", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".with"
          }), "(context)", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".reset"
          }), "()\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "opt-out",
        children: "Opt out"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "optOut"
        }), " method disabled event tracking. When called, events are not sent\nto Hightouch's servers."]
      }), "\n", _jsxs(_components.p, {
        children: ["Note that the opt out persists across device reboots. To opt back in to\ntracking, pass ", _jsx(_components.code, {
          children: "false"
        }), " to ", _jsx(_components.code, {
          children: "optOut"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-csharp",
          children: ["Analytics.", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "with"
          }), "(context).optOut(", _jsx(_components.span, {
            className: "hljs-literal",
            children: "true"
          }), ")\n"]
        })
      })]
    });
  }
}
export default MDXContent;
